import { BrButton, BrLoading, BrTag } from '@govbr-ds/react-components';
import ErrorMessage from 'components/errorMessage/errorMessage';
import Paginacao from 'components/paginacao/paginacao';
import { EFormaPagamento } from 'enums/EFormaPagamento';
import { getValueFromETipoRecolhimentoByIndex } from 'enums/ETipoRecolhimento';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';
import { IOrderField, IReceberListagemGuiasRecolhimento } from 'interfaces/IGuiaRecolhimento';
import React, { useEffect } from 'react';
import { Table } from 'react-dsgov';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { formatarDataISOpBR } from 'utils/formatDate';
import { formatarNrProcesso } from 'utils/formatNrProcesso';
import { mesParaAbreviacao } from 'utils/mesParaAbreviacao';
import styles from './listaPaginada.module.scss';

interface IListaPaginadaProps {
  listaGuiasRecolhimentoData: IReceberListagemGuiasRecolhimento | undefined;
  setOrderArray: React.Dispatch<React.SetStateAction<IOrderField[]>>;
  orderArray: IOrderField[];
  pageCount?: number;
  itemCount?: number;
  selectedPage?: number;
  selectedPageSize?: number;
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  showPageCombo?: boolean;
  onChangePage?: (pageNumber: number) => void;
  onChangeSizePage?: (pageSize: number) => void;
  isLoading?: boolean;
  error: IErrorWithSentryId | null;
  refetch: () => void;
  setIsModalExcluirGuiaOpened: React.Dispatch<React.SetStateAction<string | null>>;
}

const ListaPaginada = ({
  listaGuiasRecolhimentoData,
  setOrderArray,
  orderArray,
  pageCount,
  itemCount,
  selectedPage,
  selectedPageSize,
  pageSizeOptions,
  defaultPageSize,
  showPageCombo,
  onChangePage,
  onChangeSizePage,
  isLoading,
  error,
  refetch,
  setIsModalExcluirGuiaOpened,
}: IListaPaginadaProps) => {
  useEffect(() => {
    setOrderArray(orderArray);
  }, [orderArray]);

  const getIsColunaOrdenada = (nomeCampo: string) => {
    const orderByField = orderArray.find((order) => order.field === nomeCampo);
    return orderByField ? styles.colunaOrdenada : '';
  };

  const situacaoPagamentoTag = (situacao: string) => {
    switch (situacao) {
      case 'Vencida':
      case 'Cancelada':
      case 'Rejeitada':
        return <BrTag className={styles.tag} color='danger' value='Vencida' />;
      case 'Em preenchimento':
        return <BrTag className={styles.tag} color='secondary-07' value='Em preenchimento' />;
      case 'A pagar':
        return <BrTag className={styles.tag} color='support-01' value='A pagar' />;
      case 'Paga':
        return <BrTag className={styles.tag} color='success' value='Paga' />;
      default:
        return <span>{situacao}</span>;
    }
  };

  const getPathByTipoDeclaranteAndAcao = (tipoDeclarante: string, acao: 'editar' | 'visualizar') => {
    const rotas = {
      'Titular/Arrendatário': 'titular-ou-arrendatario',
      'Primeiro Adquirente': 'primeiro-adquirente',
    };

    const rota = rotas[tipoDeclarante as keyof typeof rotas];
    if (!rota) return '/';

    return acao === 'visualizar' ? `/${rota}-view` : `/${rota}`;
  };

  const getButtonBySituacao = (situacao: string, guiaId: string, tipoDeclarante: string) => {
    switch (situacao) {
      case 'Em preenchimento':
        return (
          <div className={styles.statusColumn}>
            <BrButton
              circle
              size='small'
              icon='edit'
              aria-label='Editar guia de recolhimento'
              title='Editar guia de recolhimento'
              onClick={() => navigate(`${getPathByTipoDeclaranteAndAcao(tipoDeclarante, 'editar')}?id=${guiaId}`)}
            />
            <BrButton
              circle
              size='small'
              icon='trash'
              aria-label='Excluir guia de recolhimento'
              title='Excluir guia de recolhimento'
              onClick={() => setIsModalExcluirGuiaOpened(guiaId)}
            />
          </div>
        );
      case 'A pagar':
      case 'Paga':
      case 'Vencida':
      case 'Cancelado':
      case 'Rejeitado':
        return (
          <BrButton
            circle
            size='small'
            icon='eye'
            aria-label='Visualizar status da guia de recolhimento'
            title='Visualizar status da guia de recolhimento'
            onClick={() => navigate(`${getPathByTipoDeclaranteAndAcao(tipoDeclarante, 'visualizar')}?id=${guiaId}`)}
          />
        );
      default:
        return null;
    }
  };

  const getIcon = (nomeCampo: string) => {
    const orderByField = orderArray.find((order) => order.field === nomeCampo);
    if (!orderByField) {
      return `fas fa-sort ${styles['sort-icon']}`;
    }
    if (orderByField.direction === 'ASC') {
      return `fas fa-sort-up ${styles['sort-icon-up']}`;
    }
    if (orderByField.direction === 'DESC') {
      return `fas fa-sort-down ${styles['sort-icon-down']}`;
    }
    return `fas fa-sort ${styles['sort-icon']}`;
  };

  const onChangeOrderBy = (nomeCampo: string, campoSecundario?: string) => {
    const newArrayOrder: IOrderField[] = [];

    const orderByField = orderArray.find((order) => order.field === nomeCampo);

    if (!orderByField) {
      newArrayOrder.unshift({ field: nomeCampo, direction: 'ASC' });
      if (campoSecundario) {
        newArrayOrder.unshift({ field: campoSecundario, direction: 'ASC' });
      }
    } else if (orderByField.direction === 'ASC') {
      newArrayOrder.unshift({ field: nomeCampo, direction: 'DESC' });

      if (campoSecundario) {
        newArrayOrder.unshift({ field: campoSecundario, direction: 'DESC' });
      }
    }
    setOrderArray(newArrayOrder);
  };

  const navigate = useNavigate();

  return (
    <div className={styles.listaContainer}>
      {error ? (
        <div className={styles.errorContainer}>
          <ErrorMessage
            errorMessage={error.response?.statusText || error.message}
            errorDescription='Ocorreu um erro na hora de trazer a lista de guias de recolhimento. Por favor, tente novamente ou entre em contato com o suporte.'
            error={error}
            onClickFunction={() => refetch()}
            buttonMessage='Tente Novamente'
          />
        </div>
      ) : (
        <>
          <Table className={styles.tabela} showSearch={false} showDensityButtons={false} showPagination={false}>
            <thead>
              <tr>
                <th className={styles.centerContainer}>Ações</th>
                <th className={styles.centerContainer}>
                  <div
                    onClick={() => onChangeOrderBy('status')}
                    onKeyDown={() => onChangeOrderBy('status')}
                    role='button'
                    tabIndex={0}
                  >
                    Situação
                    <i className={`${getIcon('status')}`}></i>
                  </div>
                </th>
                <th>
                  <div
                    onClick={() => onChangeOrderBy('tipoRecolhimento')}
                    onKeyDown={() => onChangeOrderBy('tipoRecolhimento')}
                    role='button'
                    tabIndex={0}
                  >
                    Tipo de Guia
                    <i className={`${getIcon('tipoRecolhimento')}`}></i>
                  </div>
                </th>
                <th>
                  <div
                    onClick={() => onChangeOrderBy('tipoDeclarante')}
                    onKeyDown={() => onChangeOrderBy('tipoDeclarante')}
                  >
                    Emitente
                    <i className={`${getIcon('tipoDeclarante')}`}></i>
                  </div>
                </th>
                <th>
                  <div onClick={() => onChangeOrderBy('competencia')} onKeyDown={() => onChangeOrderBy('competencia')}>
                    Competência
                    <i className={`${getIcon('competencia')}`}></i>
                  </div>
                </th>
                <th>
                  <div
                    onClick={() => onChangeOrderBy('dataVencimento')}
                    onKeyDown={() => onChangeOrderBy('dataVencimento')}
                    role='button'
                    tabIndex={0}
                  >
                    Vencimento
                    <i className={`${getIcon('dataVencimento')}`}></i>
                  </div>
                </th>
                <th>
                  <div
                    onClick={() => onChangeOrderBy('processo.numero', 'processo.ano')}
                    onKeyDown={() => onChangeOrderBy('processo.numero', 'processo.ano')}
                    role='button'
                    tabIndex={0}
                  >
                    Processo
                    <i className={`${getIcon('processo.numero')}`}></i>
                  </div>
                </th>
                <th>
                  <div
                    onClick={() => onChangeOrderBy('substanciaCorrelacionada.nome')}
                    onKeyDown={() => onChangeOrderBy('substanciaCorrelacionada.nome')}
                    role='button'
                    tabIndex={0}
                  >
                    Substância
                    <i className={`${getIcon('substanciaCorrelacionada.nome')}`}></i>
                  </div>
                </th>
                <th>
                  <div
                    onClick={() => onChangeOrderBy('municipio.nome')}
                    onKeyDown={() => onChangeOrderBy('municipio.nome')}
                    role='button'
                    tabIndex={0}
                  >
                    Município/UF
                    <i className={`${getIcon('municipio.nome')}`}></i>
                  </div>
                </th>
                <th>
                  <div
                    onClick={() => onChangeOrderBy('formaPagamento')}
                    onKeyDown={() => onChangeOrderBy('formaPagamento')}
                    role='button'
                    tabIndex={0}
                  >
                    Forma Pagamento
                    <i className={`${getIcon('formaPagamento')}`}></i>
                  </div>
                </th>
              </tr>
            </thead>

            {isLoading ? (
              <tbody>
                <tr>
                  <td colSpan={10}>
                    <BrLoading
                      large
                      label='Processando consulta externa de status do pagamento. Por favor, aguarde...'
                    />
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {listaGuiasRecolhimentoData?.content && listaGuiasRecolhimentoData.content.length > 0 ? (
                  listaGuiasRecolhimentoData?.content.map((guia, index) => (
                    <>
                      <Tooltip
                        opacity={1}
                        id='td-tipo-recolhimento'
                        place='bottom'
                        className={styles.tooltip}
                        variant='warning'
                        disableTooltip={() => guia.tipoRecolhimento === 0 || guia.tipoRecolhimento === 1}
                      >
                        <div className={styles.tooltipText}>
                          O tipo de recolhimento só é definido entre Original e Complementar após o pagamento da guia.
                        </div>
                      </Tooltip>

                      <tr key={index}>
                        <td className={styles.centerContainer}>
                          {getButtonBySituacao(guia.situacaoPagamento, guia.id, guia.tipoDeclarante)}
                        </td>
                        <td className={`${styles.centerContainer} ${getIsColunaOrdenada('status')}`}>
                          {situacaoPagamentoTag(guia.situacaoPagamento)}
                        </td>
                        <td
                          className={` ${getIsColunaOrdenada('tipoRecolhimento')}`}
                          data-tooltip-id='td-tipo-recolhimento'
                        >
                          {getValueFromETipoRecolhimentoByIndex(
                            guia.tipoRecolhimento === 0 || guia.tipoRecolhimento === 1 ? guia.tipoRecolhimento : -1
                          )}
                        </td>
                        <td className={`${styles.wrap} ${getIsColunaOrdenada('tipoDeclarante')}`}>
                          {guia.tipoDeclarante.replace('/', ' ou ')}
                        </td>
                        <td className={`${styles.wrap} ${getIsColunaOrdenada('competencia')}`}>
                          {mesParaAbreviacao(guia.competenciaMes) + ' / ' + guia.competenciaAno}
                        </td>
                        <td className={`${getIsColunaOrdenada('dataVencimento')}`}>
                          {guia.dataVencimento ? formatarDataISOpBR(guia.dataVencimento) : '-'}
                        </td>
                        <td className={`${getIsColunaOrdenada('processo.numero')}`}>
                          {formatarNrProcesso(guia.numeroProcesso)}
                        </td>
                        <td className={`${getIsColunaOrdenada('substancia.nome')}`}>{guia.substancia}</td>
                        <td className={`${getIsColunaOrdenada('municipio.nome')}`}>
                          {guia.municipio.nome + ' / ' + guia.municipio.uf}
                        </td>
                        <td className={`${getIsColunaOrdenada('formaPagamento')}`}>
                          {EFormaPagamento[guia.formaPagamento as keyof typeof EFormaPagamento] || ''}
                        </td>
                      </tr>
                    </>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10}>Nenhum registro encontrado</td>
                  </tr>
                )}
              </tbody>
            )}
          </Table>
          {listaGuiasRecolhimentoData && listaGuiasRecolhimentoData.content.length !== 0 && (
            <div className={styles.paginacao}>
              <Paginacao
                pageCount={pageCount}
                itemCount={itemCount}
                selectedPage={selectedPage}
                selectedPageSize={selectedPageSize}
                onChangePage={onChangePage}
                onChangeSizePage={onChangeSizePage}
                pageSizeOptions={pageSizeOptions}
                defaultPageSize={defaultPageSize}
                showPageCombo={showPageCombo}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ListaPaginada;
